import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const DISCIPLINES = [
  { v: "muay_thai", label: "Muay Thaï" },
  { v: "kickboxing", label: "Kickboxing" },
  { v: "muay_thai_kickboxing", label: "Kickboxing / Muay Thaï" },
  { v: "jiu_jitsu_bresilien", label: "JJB" },
  { v: "jiu_jitsu_can_ryu", label: "Jiu-jitsu Can Ryu" },
  { v: "jiu_jitsu_kids", label: "JJ Kids" },
  { v: "jujutsu_japonais", label: "Jiu-jitsu Can Ryu (ancien)" },
  { v: "power_kicker", label: "Power Kicker" },
  { v: "mma", label: "MMA" },
  { v: "autre", label: "Autre" },
];

const GROUPS = [
  { v: "petits_samourais_4_7", label: "Petits Samouraïs (3-6 ans)" },
  { v: "kids_4_7", label: "Parent-Enfant 3-6 ans" },
  { v: "kids_8_12", label: "Kids 8-12" },
  { v: "juniors_8_13", label: "Juniors 8-13" },
  { v: "ados_13_17", label: "Ados 13-17" },
  { v: "adultes_18_plus", label: "Adultes 18+" },
  { v: "mixte", label: "Mixte" },
];

// Ceintures suggérées par discipline (texte libre permis)
const BELT_OPTIONS = {
  muay_thai: ["Blanche", "Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire", "Mongkhon"],
  muay_thai_kickboxing: ["Blanche", "Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire", "Mongkhon"],
  kickboxing: ["Blanche", "Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire"],
  jiu_jitsu_bresilien: ["Blanche", "Grise", "Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire"],
  jiu_jitsu_can_ryu: ["Blanche", "Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire"],
  jiu_jitsu_kids: ["Blanche", "Grise", "Jaune", "Orange", "Verte"],
  jujutsu_japonais: ["Blanche", "Jaune", "Orange", "Verte", "Bleue", "Mauve", "Brune", "Noire"],
  power_kicker: ["Découverte", "6 mois", "1 an", "Avancée"],
  mma: ["Niveau 1", "Niveau 2", "Niveau 3", "Niveau 4"],
  autre: [],
};

function suggestGroupFromAge(age) {
  if (age == null || age === "") return null;
  const a = Number(age);
  if (a >= 3 && a <= 6) return "petits_samourais_4_7";
  if (a >= 8 && a <= 13) return "juniors_8_13";
  if (a >= 14 && a <= 17) return "ados_13_17";
  if (a >= 18) return "adultes_18_plus";
  return null;
}

function computeAutoFillRules(contracts) {
  /** Retourne {rules: [{id, label, count, apply: (c) => patch}], previewById: {id: patch}} */
  const rules = [];
  const previewById = {}; // {contractId: {merged patch}}

  const addPreview = (cid, patch) => {
    previewById[cid] = { ...(previewById[cid] || {}), ...patch };
  };

  // Rule 1 : âge → groupe (si groupe manquant)
  const byAgeGroup = contracts.filter((c) => c.age != null && !c.groupe_age);
  if (byAgeGroup.length > 0) {
    let count = 0;
    byAgeGroup.forEach((c) => {
      const g = suggestGroupFromAge(c.age);
      if (g) {
        addPreview(c.id, { groupe_age: g });
        count++;
      }
    });
    if (count > 0) {
      rules.push({
        id: "age_to_group",
        label: `🎯 ${count} élèves : groupe d'âge auto-déduit (âge → groupe)`,
        count,
      });
    }
  }

  // Rule 2 : Muay Thaï / Kickboxing sans ceinture → Blanche
  const mtNoBelt = contracts.filter(
    (c) =>
      !c.niveau_ceinture &&
      ["muay_thai", "kickboxing", "muay_thai_kickboxing"].includes(c.discipline),
  );
  if (mtNoBelt.length > 0) {
    mtNoBelt.forEach((c) => addPreview(c.id, { niveau_ceinture: "Blanche" }));
    rules.push({
      id: "mt_default_white",
      label: `🥊 ${mtNoBelt.length} élèves Muay Thaï/Kick sans ceinture → Blanche par défaut`,
      count: mtNoBelt.length,
    });
  }

  // Rule 3 : JJB sans ceinture
  const bjjNoBelt = contracts.filter(
    (c) =>
      !c.niveau_ceinture &&
      ["jiu_jitsu_bresilien", "jiu_jitsu_kids"].includes(c.discipline),
  );
  if (bjjNoBelt.length > 0) {
    bjjNoBelt.forEach((c) => {
      // Kids JJB → Grise par défaut si <14 ans, sinon Blanche
      const belt = (c.age != null && c.age < 14) ? "Grise" : "Blanche";
      addPreview(c.id, { niveau_ceinture: belt });
    });
    rules.push({
      id: "bjj_default_white",
      label: `🥋 ${bjjNoBelt.length} élèves JJB sans ceinture → Blanche/Grise selon âge`,
      count: bjjNoBelt.length,
    });
  }

  // Rule 4 : JJ Can Ryu / Jujutsu sans ceinture → Blanche
  const jjNoBelt = contracts.filter(
    (c) =>
      !c.niveau_ceinture &&
      ["jiu_jitsu_can_ryu", "jujutsu_japonais"].includes(c.discipline),
  );
  if (jjNoBelt.length > 0) {
    jjNoBelt.forEach((c) => addPreview(c.id, { niveau_ceinture: "Blanche" }));
    rules.push({
      id: "jj_default_white",
      label: `🇯🇵 ${jjNoBelt.length} élèves JJ Can Ryu sans ceinture → Blanche`,
      count: jjNoBelt.length,
    });
  }

  // Rule 5 : Power Kicker sans niveau → Découverte
  const pkNoBelt = contracts.filter(
    (c) => !c.niveau_ceinture && c.discipline === "power_kicker",
  );
  if (pkNoBelt.length > 0) {
    pkNoBelt.forEach((c) => addPreview(c.id, { niveau_ceinture: "Découverte" }));
    rules.push({
      id: "pk_default_decouverte",
      label: `💪 ${pkNoBelt.length} élèves Power Kicker → niveau Découverte`,
      count: pkNoBelt.length,
    });
  }

  const totalUnique = Object.keys(previewById).length;
  return { rules, previewById, totalUnique };
}

export default function BulkEditElevesModule() {
  const [contracts, setContracts] = useState([]);
  const [dirty, setDirty] = useState({}); // {contract_id: {field: value}}
  const [filter, setFilter] = useState("all"); // all | missing_belt | missing_group | missing_discipline | missing_age
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [autofillSelected, setAutofillSelected] = useState({});

  // 🛠️ Outils admin : fusion de disciplines + ajout manuel d'élève
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mergeSources, setMergeSources] = useState({ kickboxing: true, muay_thai: true, muay_thai_kickboxing: true });
  const [mergeTarget, setMergeTarget] = useState("muay_thai_kickboxing");
  const [merging, setMerging] = useState(false);
  const [lastUndoId, setLastUndoId] = useState(null);

  // ⚡ Normalisation auto vers les 3 disciplines officielles (auto-guérison prod)
  const [normalizing, setNormalizing] = useState(false);
  const [normReport, setNormReport] = useState(null);
  const [normUndoId, setNormUndoId] = useState(null);

  const runNormalize = async (dryRun) => {
    if (!dryRun && !window.confirm("Appliquer la normalisation vers les 3 disciplines officielles dans TOUTES les collections ? (annulable en 1 clic)")) return;
    setNormalizing(true);
    try {
      const r = await api.post(`/admin/discipline/normalize?dry_run=${dryRun}`);
      const rep = r.data.report;
      setNormReport(rep);
      const total = Object.values(rep.changes_per_collection || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      if (dryRun) {
        toast.success(total ? `Aperçu : ${total} dossier(s) à normaliser.` : "Tout est déjà conforme aux 3 disciplines ✅");
      } else {
        toast.success(total ? `Normalisation faite : ${total} dossier(s) migrés.` : "Rien à changer — déjà conforme ✅");
        if (rep.undo_id) setNormUndoId(rep.undo_id);
        load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la normalisation."); }
    setNormalizing(false);
  };

  const undoNormalize = async () => {
    if (!normUndoId || !window.confirm("Annuler la dernière normalisation et restaurer les disciplines d'origine ?")) return;
    try {
      const r = await api.post(`/admin/discipline/undo/${normUndoId}`);
      toast.success(`Normalisation annulée — ${r.data.restored} dossier(s) restauré(s).`);
      setNormUndoId(null); setNormReport(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Annulation impossible."); }
  };

  const undoMerge = async () => {
    if (!lastUndoId || !window.confirm("Annuler la dernière fusion et restaurer les disciplines d'origine ?")) return;
    try {
      const r = await api.post(`/admin/discipline/undo/${lastUndoId}`);
      toast.success(`Fusion annulée — ${r.data.restored} dossier(s) restauré(s).`);
      setLastUndoId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Annulation impossible."); }
  };
  const [newStudent, setNewStudent] = useState({ full_name: "Alexis Cadieux", discipline: "muay_thai_kickboxing", niveau_ceinture: "Verte", email: "alexis.cadieux16@gmail.com", phone: "+18195509933", date_of_birth: "2004-08-16" });
  const [addingStudent, setAddingStudent] = useState(false);

  const runMerge = async (dryRun) => {
    const sources = Object.keys(mergeSources).filter((k) => mergeSources[k] && k !== mergeTarget);
    if (!sources.length) { toast.error("Choisis au moins une discipline source (≠ cible)."); return; }
    if (!dryRun && !window.confirm(`Fusionner [${sources.join(", ")}] → ${mergeTarget} dans TOUTES les collections ? Opération irréversible.`)) return;
    setMerging(true);
    try {
      const r = await api.post("/admin/discipline/merge", { sources, target: mergeTarget, dry_run: dryRun });
      const rep = r.data.report;
      const total = Object.values(rep.merged_per_collection || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(dryRun ? `Aperçu : ${total} enregistrement(s) seraient fusionnés.` : `Fusion faite : ${total} enregistrement(s) migrés vers ${mergeTarget}.`);
      if (!dryRun) { setLastUndoId(rep.undo_id); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la fusion."); }
    setMerging(false);
  };

  const addStudent = async () => {
    if (!newStudent.full_name.trim()) { toast.error("Nom requis."); return; }
    setAddingStudent(true);
    try {
      await api.post("/inscriptions/manual", newStudent);
      toast.success(`${newStudent.full_name} ajouté(e) !`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'ajout."); }
    setAddingStudent(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/student-contracts");
      setContracts(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Erreur chargement contrats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const merged = useMemo(() => {
    return contracts.map((c) => ({ ...c, ...(dirty[c.id] || {}) }));
  }, [contracts, dirty]);

  const filtered = useMemo(() => {
    let list = merged;
    if (filter === "missing_belt") list = list.filter((c) => !c.niveau_ceinture);
    if (filter === "missing_group") list = list.filter((c) => !c.groupe_age);
    if (filter === "missing_discipline") list = list.filter((c) => !c.discipline || c.discipline === "autre");
    if (filter === "missing_age") list = list.filter((c) => c.age == null);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((c) => (c.full_name || "").toLowerCase().includes(s));
    }
    return list.sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
  }, [merged, filter, search]);

  const stats = useMemo(() => ({
    total: contracts.length,
    missing_belt: contracts.filter((c) => !c.niveau_ceinture).length,
    missing_group: contracts.filter((c) => !c.groupe_age).length,
    missing_discipline: contracts.filter((c) => !c.discipline || c.discipline === "autre").length,
    missing_age: contracts.filter((c) => c.age == null).length,
    dirty_count: Object.keys(dirty).length,
  }), [contracts, dirty]);

  const setField = (id, field, value) => {
    setDirty((d) => {
      const next = { ...d };
      const row = { ...(next[id] || {}) };
      if (value === "" || value == null) {
        delete row[field];
      } else {
        row[field] = value;
      }
      // Auto-suggest groupe_age when age changes
      if (field === "age") {
        const suggested = suggestGroupFromAge(value);
        const original = contracts.find((c) => c.id === id);
        const currentGroup = row.groupe_age || original?.groupe_age;
        if (suggested && !currentGroup) {
          row.groupe_age = suggested;
        }
      }
      if (Object.keys(row).length === 0) {
        delete next[id];
      } else {
        next[id] = row;
      }
      return next;
    });
  };

  const saveAll = async () => {
    const updates = Object.entries(dirty).map(([id, fields]) => ({ id, ...fields }));
    if (updates.length === 0) {
      toast.info("Rien à sauvegarder");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/student-contracts/bulk-update", { updates });
      toast.success(`✅ ${r.data.updated} contrats mis à jour · ${r.data.skipped} skip · ${r.data.errors.length} erreurs`);
      if (r.data.errors.length > 0) {
        console.warn("Bulk update errors:", r.data.errors);
      }
      setDirty({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const autofill = useMemo(() => computeAutoFillRules(merged), [merged]);

  const openAutofill = () => {
    // Sélectionne tout par défaut
    const sel = {};
    autofill.rules.forEach((r) => { sel[r.id] = true; });
    setAutofillSelected(sel);
    setAutofillOpen(true);
  };

  const applyAutofill = () => {
    /** Pour chaque règle cochée, recompute juste les patches concernés et merge dans `dirty`. */
    const activeRuleIds = new Set(Object.entries(autofillSelected).filter(([, v]) => v).map(([k]) => k));
    if (activeRuleIds.size === 0) {
      toast.info("Aucune règle sélectionnée");
      return;
    }
    // Re-compute par règle pour mapper les patches au bon contrat
    const patchesByContract = {};
    const addPatch = (cid, patch) => {
      patchesByContract[cid] = { ...(patchesByContract[cid] || {}), ...patch };
    };

    merged.forEach((c) => {
      if (activeRuleIds.has("age_to_group") && c.age != null && !c.groupe_age) {
        const g = suggestGroupFromAge(c.age);
        if (g) addPatch(c.id, { groupe_age: g });
      }
      if (activeRuleIds.has("mt_default_white") && !c.niveau_ceinture && ["muay_thai", "kickboxing", "muay_thai_kickboxing"].includes(c.discipline)) {
        addPatch(c.id, { niveau_ceinture: "Blanche" });
      }
      if (activeRuleIds.has("bjj_default_white") && !c.niveau_ceinture && ["jiu_jitsu_bresilien", "jiu_jitsu_kids"].includes(c.discipline)) {
        addPatch(c.id, { niveau_ceinture: (c.age != null && c.age < 14) ? "Grise" : "Blanche" });
      }
      if (activeRuleIds.has("jj_default_white") && !c.niveau_ceinture && ["jiu_jitsu_can_ryu", "jujutsu_japonais"].includes(c.discipline)) {
        addPatch(c.id, { niveau_ceinture: "Blanche" });
      }
      if (activeRuleIds.has("pk_default_decouverte") && !c.niveau_ceinture && c.discipline === "power_kicker") {
        addPatch(c.id, { niveau_ceinture: "Découverte" });
      }
    });

    const affected = Object.keys(patchesByContract).length;
    if (affected === 0) {
      toast.info("Aucun changement à appliquer");
      setAutofillOpen(false);
      return;
    }

    setDirty((d) => {
      const next = { ...d };
      Object.entries(patchesByContract).forEach(([cid, patch]) => {
        next[cid] = { ...(next[cid] || {}), ...patch };
      });
      return next;
    });
    toast.success(`🪄 ${affected} élèves pré-remplis — clique "Sauvegarder tout" pour confirmer`);
    setAutofillOpen(false);
  };

  if (loading) return <div className="text-zinc-500 text-sm">Chargement contrats…</div>;

  return (
    <div className="space-y-6" data-testid="bulk-edit-eleves-module">
      {/* Header */}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">CARNETS · ÉDITION RAPIDE</div>
        <h1 className="text-3xl font-bold tracking-tight">Bulk-edit élèves</h1>
        <p className="text-zinc-500 text-sm mt-2">Comble les ceintures, groupes d'âge, disciplines manquantes. Modifie ce que tu veux puis clique "Sauvegarder tout".</p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          <StatChip label="Total" value={stats.total} testId="stat-total" />
          <StatChip label="Sans ceinture" value={stats.missing_belt} red={stats.missing_belt > 0} testId="stat-missing-belt" />
          <StatChip label="Sans groupe" value={stats.missing_group} red={stats.missing_group > 0} testId="stat-missing-group" />
          <StatChip label="Sans discipline" value={stats.missing_discipline} red={stats.missing_discipline > 0} testId="stat-missing-discipline" />
          <StatChip label="Sans âge" value={stats.missing_age} red={stats.missing_age > 0} testId="stat-missing-age" />
        </div>
      </div>

      {/* Filters + Save */}
      {/* 🛠️ Outils admin : fusion de disciplines + ajout d'élève (alumni) */}
      <div className="border border-amber-500/30 rounded-2xl bg-amber-500/5" data-testid="admin-tools-panel">
        <button onClick={() => setToolsOpen((o) => !o)} data-testid="admin-tools-toggle"
          className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-amber-200">
          🛠️ Outils admin — fusion de disciplines & ajout d'élève
          <span>{toolsOpen ? "▲" : "▼"}</span>
        </button>
        {toolsOpen && (
          <div className="px-5 pb-5 space-y-6">
            {/* ⚡ Normalisation auto — action recommandée (3 disciplines officielles) */}
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3" data-testid="normalize-panel">
              <h3 className="text-sm font-bold text-emerald-200">⚡ Normalisation auto — 3 disciplines officielles</h3>
              <p className="text-xs text-zinc-400">Regroupe automatiquement tous les variants vers <b>Jiu-jitsu Can Ryu</b> (inclut Kids / Petits Samouraïs), <b>Jiu-jitsu brésilien</b> et <b>Kickboxing / Muay Thaï</b> (inclut PowerKick Her). Idempotent · annulable. Les dossiers « autre » (Drop-In / facturation) sont laissés intacts.</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => runNormalize(true)} disabled={normalizing} data-testid="normalize-preview-btn">{normalizing ? "…" : "Aperçu (dry-run)"}</Button>
                <Button size="sm" onClick={() => runNormalize(false)} disabled={normalizing} data-testid="normalize-run-btn" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950">{normalizing ? "…" : "Appliquer"}</Button>
                {normUndoId && (
                  <Button variant="outline" size="sm" onClick={undoNormalize} data-testid="normalize-undo-btn" className="border-red-500/40 text-red-300">↩ Annuler</Button>
                )}
              </div>
              {normReport && (
                <div className="text-xs text-zinc-300 bg-zinc-950/60 rounded-lg p-3 space-y-1" data-testid="normalize-report">
                  {Object.keys(normReport.mapping || {}).length === 0 ? (
                    <p className="text-emerald-300">✅ Aucun changement — tout est conforme aux 3 disciplines.</p>
                  ) : (
                    Object.entries(normReport.mapping).map(([k, v]) => <div key={k}>• {k} <span className="text-amber-300">({v})</span></div>)
                  )}
                  {normReport.unmapped && Object.keys(normReport.unmapped).length > 0 && (
                    <p className="text-zinc-500 pt-1">Non mappé (laissé intact) : {Object.entries(normReport.unmapped).map(([c, arr]) => `${c}: ${arr.join(", ")}`).join(" · ")}</p>
                  )}
                </div>
              )}
            </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-zinc-200">Fusionner des disciplines</h3>
              <p className="text-xs text-zinc-500">Coche les disciplines à fusionner, choisis la cible. Tous les élèves, carnets, contrats et techniques seront regroupés.</p>
              <div className="flex flex-wrap gap-2">
                {DISCIPLINES.map((d) => (
                  <label key={d.v} className="flex items-center gap-1.5 text-xs bg-zinc-900 px-2 py-1 rounded cursor-pointer">
                    <input type="checkbox" checked={!!mergeSources[d.v]} onChange={(e) => setMergeSources((s) => ({ ...s, [d.v]: e.target.checked }))} className="accent-amber-500" />
                    {d.label}
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-400">Cible :</span>
                <Select value={mergeTarget} onValueChange={setMergeTarget}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 h-9 max-w-[220px]" data-testid="merge-target"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => runMerge(true)} disabled={merging} data-testid="merge-preview-btn">Aperçu</Button>
                <Button size="sm" onClick={() => runMerge(false)} disabled={merging} data-testid="merge-run-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-950">{merging ? "…" : "Fusionner"}</Button>
                {lastUndoId && (
                  <Button variant="outline" size="sm" onClick={undoMerge} data-testid="merge-undo-btn" className="border-red-500/40 text-red-300">↩ Annuler la fusion</Button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-zinc-200">Ajouter un élève (ancien membre ZenPlanner)</h3>
              <div className="grid grid-cols-2 gap-2">
                <Input value={newStudent.full_name} onChange={(e) => setNewStudent((s) => ({ ...s, full_name: e.target.value }))} placeholder="Nom complet" className="bg-zinc-950 border-zinc-800 h-9 col-span-2" data-testid="add-student-name" />
                <Select value={newStudent.discipline} onValueChange={(v) => setNewStudent((s) => ({ ...s, discipline: v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={newStudent.niveau_ceinture} onChange={(e) => setNewStudent((s) => ({ ...s, niveau_ceinture: e.target.value }))} placeholder="Ceinture" className="bg-zinc-950 border-zinc-800 h-9" data-testid="add-student-belt" />
                <Input value={newStudent.email} onChange={(e) => setNewStudent((s) => ({ ...s, email: e.target.value }))} placeholder="Courriel" className="bg-zinc-950 border-zinc-800 h-9" />
                <Input value={newStudent.phone} onChange={(e) => setNewStudent((s) => ({ ...s, phone: e.target.value }))} placeholder="Téléphone" className="bg-zinc-950 border-zinc-800 h-9" />
              </div>
              <Button size="sm" onClick={addStudent} disabled={addingStudent} data-testid="add-student-btn" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950">{addingStudent ? "Ajout…" : "Ajouter l'élève"}</Button>
            </div>
          </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="bulk-edit-toolbar">
        <Input
          placeholder="🔍 Rechercher un élève…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-zinc-950 border-zinc-800 max-w-xs"
          data-testid="bulk-edit-search"
        />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 max-w-xs" data-testid="bulk-edit-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous ({stats.total})</SelectItem>
            <SelectItem value="missing_belt">Sans ceinture ({stats.missing_belt})</SelectItem>
            <SelectItem value="missing_group">Sans groupe ({stats.missing_group})</SelectItem>
            <SelectItem value="missing_discipline">Sans discipline ({stats.missing_discipline})</SelectItem>
            <SelectItem value="missing_age">Sans âge ({stats.missing_age})</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {stats.dirty_count > 0 && (
          <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid="dirty-count-badge">
            {stats.dirty_count} modifications en attente
          </Badge>
        )}
        <Button
          onClick={openAutofill}
          variant="outline"
          disabled={autofill.totalUnique === 0}
          className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
          data-testid="autofill-open-btn"
          title={autofill.totalUnique === 0 ? "Rien à pré-remplir" : `${autofill.totalUnique} élèves touchés`}
        >
          🪄 Auto-remplir ({autofill.totalUnique})
        </Button>
        <Button
          onClick={saveAll}
          disabled={saving || stats.dirty_count === 0}
          className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
          data-testid="bulk-edit-save-btn"
        >
          {saving ? "Sauvegarde…" : `💾 Sauvegarder tout (${stats.dirty_count})`}
        </Button>
      </div>

      {/* Table */}
      <div className="border border-zinc-800 rounded-2xl overflow-hidden" data-testid="bulk-edit-table-wrapper">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/50 text-zinc-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-3 text-left">Élève</th>
                <th className="px-3 py-3 text-left w-20">Âge</th>
                <th className="px-3 py-3 text-left w-40">Discipline</th>
                <th className="px-3 py-3 text-left w-40">Ceinture</th>
                <th className="px-3 py-3 text-left w-40">Groupe</th>
                <th className="px-3 py-3 text-left w-24">Sessions/sem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-zinc-600">Aucun élève dans ce filtre.</td>
                </tr>
              ) : (
                filtered.map((c) => {
                  const isDirty = dirty[c.id] != null;
                  return (
                    <tr
                      key={c.id}
                      className={`hover:bg-zinc-950/40 ${isDirty ? "bg-amber-500/5" : ""}`}
                      data-testid={`row-${c.id}`}
                    >
                      <td className="px-3 py-2 font-medium text-zinc-200">
                        <div className="flex items-center gap-2">
                          {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Modifié" />}
                          {c.full_name}
                        </div>
                        {c.parent_email && <div className="text-[10px] text-zinc-600 mt-0.5">{c.parent_email}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          value={c.age ?? ""}
                          onChange={(e) => setField(c.id, "age", e.target.value ? Number(e.target.value) : null)}
                          className="bg-zinc-950 border-zinc-800 h-8 w-16 text-xs"
                          data-testid={`age-input-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={c.discipline || ""}
                          onValueChange={(v) => setField(c.id, "discipline", v)}
                        >
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs" data-testid={`discipline-select-${c.id}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            {DISCIPLINES.map((d) => (
                              <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <BeltInput
                          discipline={c.discipline}
                          value={c.niveau_ceinture || ""}
                          onChange={(v) => setField(c.id, "niveau_ceinture", v)}
                          testId={`belt-input-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={c.groupe_age || ""}
                          onValueChange={(v) => setField(c.id, "groupe_age", v)}
                        >
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs" data-testid={`group-select-${c.id}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            {GROUPS.map((g) => (
                              <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min="1" max="7"
                          value={c.sessions_per_week ?? 1}
                          onChange={(e) => setField(c.id, "sessions_per_week", Number(e.target.value))}
                          className="bg-zinc-950 border-zinc-800 h-8 w-14 text-xs"
                          data-testid={`sessions-input-${c.id}`}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-zinc-600 text-center pt-2">
        Astuce Kaizen : modifie l'âge → le groupe est suggéré automatiquement. Sauvegarde une fois quand tout te plaît.
      </div>

      {/* Auto-fill Dialog */}
      <Dialog open={autofillOpen} onOpenChange={setAutofillOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl" data-testid="autofill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🪄 Auto-remplir par règle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {autofill.rules.length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center">
                Aucune règle applicable — tes carnets sont déjà bien remplis 👏
              </div>
            ) : (
              autofill.rules.map((r) => (
                <label
                  key={r.id}
                  className="flex items-start gap-3 border border-zinc-800 rounded-xl p-4 cursor-pointer hover:border-purple-500/40 transition"
                  data-testid={`autofill-rule-${r.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!autofillSelected[r.id]}
                    onChange={(e) => setAutofillSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                    className="mt-1 accent-purple-400"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-zinc-200">{r.label}</div>
                  </div>
                </label>
              ))
            )}
          </div>
          {autofill.totalUnique > 0 && (
            <div className="text-[11px] text-zinc-500 italic mt-2">
              ⚠️ L'auto-remplissage prépare les changements en local. Tu cliques ensuite "💾 Sauvegarder tout" pour confirmer.
              Tu peux toujours ajuster ligne par ligne avant de sauvegarder.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutofillOpen(false)}>Annuler</Button>
            <Button
              onClick={applyAutofill}
              disabled={autofill.rules.length === 0}
              className="bg-purple-500 hover:bg-purple-400 text-white font-semibold"
              data-testid="autofill-apply-btn"
            >
              🪄 Appliquer les règles cochées
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatChip({ label, value, red, testId }) {
  return (
    <div
      className={`rounded-xl border p-3 ${red ? "border-red-500/40 bg-red-500/5" : "border-zinc-800 bg-zinc-950/50"}`}
      data-testid={testId}
    >
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${red ? "text-red-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function BeltInput({ discipline, value, onChange, testId }) {
  const options = BELT_OPTIONS[discipline] || [];
  const [customMode, setCustomMode] = useState(value && !options.includes(value));

  if (customMode || options.length === 0) {
    return (
      <div className="flex gap-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ex. Bleue +2"
          className="bg-zinc-950 border-zinc-800 h-8 text-xs"
          data-testid={testId}
        />
        {options.length > 0 && (
          <button
            type="button"
            onClick={() => setCustomMode(false)}
            className="text-[10px] text-zinc-500 hover:text-amber-300 px-1"
            title="Revenir aux choix"
          >
            ⋯
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-1">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs" data-testid={testId}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((b) => (
            <SelectItem key={b} value={b}>{b}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        onClick={() => setCustomMode(true)}
        className="text-[10px] text-zinc-500 hover:text-amber-300 px-1"
        title="Saisie libre"
      >
        ✎
      </button>
    </div>
  );
}
